import SEO from '../SEO';
import { ShoppingCart, CreditCard, Package, Truck, BarChart3, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import DomainChecker from '../DomainChecker';
import { Link } from 'react-router-dom';

function EcommerceArlington() {
  const platforms = [
    { name: "Shopify", desc: "Perfect for growing Arlington retailers", features: ["Easy management", "Secure payments", "Mobile optimized"] },
    { name: "WooCommerce", desc: "Flexible WordPress solution", features: ["Full customization", "No transaction fees", "Unlimited products"] },
    { name: "Custom Build", desc: "Enterprise-level solutions", features: ["Fully custom", "API integrations", "Scalable architecture"] }
  ];

  const features = [
    { icon: <ShoppingCart className="h-8 w-8" />, title: "Shopping Cart", desc: "Intuitive cart experience that maximizes conversions" },
    { icon: <CreditCard className="h-8 w-8" />, title: "Secure Payments", desc: "PCI-compliant payment processing with all major providers" },
    { icon: <Package className="h-8 w-8" />, title: "Inventory Management", desc: "Real-time stock tracking and automated alerts" },
    { icon: <Truck className="h-8 w-8" />, title: "Shipping Integration", desc: "UPS, FedEx, USPS with real-time rate calculation" },
    { icon: <BarChart3 className="h-8 w-8" />, title: "Analytics", desc: "Track sales, conversions, and customer behavior" },
    { icon: <Shield className="h-8 w-8" />, title: "Security", desc: "SSL certificates and fraud protection included" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="eCommerce Website Development Arlington TX | Online Store Design"
        description="Professional eCommerce development in Arlington, Texas. Shopify, WooCommerce, and custom online stores. Sell online with a powerful Arlington eCommerce website."
        keywords="ecommerce Arlington TX, online store Arlington, Shopify developer Arlington, WooCommerce Arlington Texas, ecommerce website Arlington, Arlington online store design"
        canonical="https://web-techservices.com/services/ecommerce-arlington"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-800 to-blue-900 text-white py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <span className="bg-blue-500/30 text-blue-200 px-4 py-1 rounded-full text-sm font-medium">Arlington eCommerce Experts</span>
          <h1 className="text-4xl md:text-6xl font-bold mt-6 mb-6">
            eCommerce Solutions<br />for Arlington Businesses
          </h1>
          <p className="text-xl text-blue-200 mb-8 max-w-2xl">
            Launch your online store and sell to customers in Arlington and beyond. Custom eCommerce websites built for growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/book-consultation">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-8">
                Start Selling Online <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center mb-12">eCommerce Platforms We Build On</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {platforms.map((p, i) => (
            <Card key={i} className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-2">{p.name}</h3>
                <p className="text-slate-600 mb-6">{p.desc}</p>
                <ul className="space-y-2">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Everything Your Online Store Needs</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {f.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Sell Online?</h2>
          <p className="text-xl text-blue-100 mb-8">Let's build an eCommerce website that grows your Arlington business.</p>
          <Link to="/book-consultation">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-bold">
              Get eCommerce Quote
            </Button>
          </Link>
        </div>
      </section>

      {/* Domain Checker */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4 max-w-2xl">
          <DomainChecker headline="Get Your eCommerce Domain" subtitle="Secure a custom domain for your online store — starting at $15.99/year" compact={true} />
        </div>
      </section>

    </div>
  );
}

export default EcommerceArlington;
